import { withStyles } from '@material-ui/core/styles';
import TextField, { BaseTextFieldProps } from '@material-ui/core/TextField';
import React, { HTMLAttributes } from 'react';
import Select from 'react-select';
import { ControlProps } from 'react-select/src/components/Control';
import { ValueType } from 'react-select/src/types';

import {
  IOption,
  IOptionType,
  IOptionValues,
  IProps,
  IState,
} from './interfaces';

const styles: any = (theme: any) => ({
  divider: {
    height: theme.spacing(2),
  },
  input: {
    display: 'flex',
    height: 'auto',
    padding: 10,
  },
  paper: {
    left: 0,
    marginTop: theme.spacing(1),
    position: 'absolute',
    right: 0,
    zIndex: 1,
  },
  root: {
    flexGrow: 1,
    height: 250,
    minWidth: 290,
  },
});

const customStyles = {
  container: (inheritStyles: any) => ({
    ...inheritStyles,
    margin: '20px 0',
  }),
  control: (inheritStyles: any) => ({
    ...inheritStyles,
    marginBottom: 10,
    minHeight: 50,
  }),
  menu: (inheritStyles: any) => ({
    ...inheritStyles,
    margin: 5,
    zIndex: 1111,
  }),
  menuList: (inheritStyles: any) => ({
    ...inheritStyles,
    maxHeight: 150,
  }),
  multiValue: (inheritStyles: any) => ({
    ...inheritStyles,
    borderRadius: 20,
    fontSize: 18,
    padding: 2,
  }),
  option: (inheritStyles: any, { isFocused, isSelected }: any) => ({
    ...inheritStyles,
    backgroundColor: isSelected || isFocused ? 'rgba(0, 0, 0, 0.14)' : null,
  }),
};

type InputComponentProps = Pick<BaseTextFieldProps, 'inputRef'> &
  HTMLAttributes<HTMLDivElement>;

function inputComponent({ inputRef, ...props }: InputComponentProps) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: ControlProps<IOptionType>) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;

  return (
    <TextField
      fullWidth={true}
      variant="outlined"
      InputProps={{
        inputComponent,
        inputProps: {
          children,
          className: classes.input,
          ref: innerRef,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

const components: any = {
  Control,
};

class MultiChoice extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const { options, mappedOptions } = this.getOptions(props);

    this.state = {
      mappedOptions,
      options,
      selectedOptions: null,
    };
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    const { dictionary } = this.props;

    if (!dictionary) {
      return;
    }

    if (
      this.props[dictionary as keyof IProps] !==
      prevProps[dictionary as keyof IProps]
    ) {
      const { options, mappedOptions } = this.getOptions(this.props);

      this.setState({
        mappedOptions,
        options,
      });
    }
  }

  public render() {
    const { input, title, classes } = this.props;
    const { options } = this.state;

    const values = this.getValues(input.value);

    return (
      <Select
        classes={classes}
        TextFieldProps={{
          InputLabelProps: {
            htmlFor: input.name,
            shrink: true,
          },
          label: title,
        }}
        placeholder={title}
        options={options}
        value={values}
        components={components}
        onChange={this.handleChange}
        isMulti={true}
        styles={customStyles}
        menuPlacement="auto"
      />
    );
  }

  private readonly handleChange = (
    value: ValueType<Record<string, string>>,
  ) => {
    const {
      input: { onChange },
    } = this.props;

    onChange(
      Array.isArray(value) ? value.map((item: IOption) => item.value) : [],
    );
  };

  private readonly getOptions = (props: IProps) => {
    const {
      options: propsOptions,
      optionsMap: propsOptionsMap,
      dictionary,
    } = props;

    const opts = propsOptions || this.props[dictionary as keyof IProps] || [];

    const options: IOption[] = propsOptionsMap
      ? opts.map((item: IOption) => ({
          label: item.name || item[propsOptionsMap.label],
          value: item[propsOptionsMap.value],
        }))
      : opts.map((item: IOption) => ({
          ...item,
          label: item.name || item.label,
        }));

    const mappedOptions = options.reduce(
      (acc: Record<string, IOption>, item: IOption) => {
        acc[item.value] = item;
        return acc;
      },
      {} as Record<string, IOption>,
    );

    return {
      mappedOptions,
      options,
    };
  };

  private readonly getValues = (values: IOptionValues) => {
    const { mappedOptions } = this.state;

    if (!Array.isArray(values)) {
      return [];
    }

    return values.map((value: any) => mappedOptions[value]);
  };
}

export default withStyles(styles)(MultiChoice);
